// Desktop: every picture a fill on stage shows stays open — cameras and the screen through the source manager, the first file on the transport.
import store from '~/store'
import { selectKeepCamerasArmed } from '~/store/settings/selectors'
import sourceManager from '.'
import { fillPictures } from './fillSources'
import { onFileCatalog } from './files'
import { NODE, stageFile, startFileStage } from './fileStage'
import { isFileSource } from './ids'
import { kindOf } from './manager'
import { cue, dropCues } from './playhead'
import { stagedId } from './videoPool'

const logger = console.scope('fillStage')

// The streams this module holds a ref on.
const held = new Set()
let bridge = null
let screenPrompt = false
let last = {}

/** A click picked the screen: its capture may prompt once. Any other way onto stage takes only a screen already live. */
export const allowScreenPrompt = () => {
	screenPrompt = true
}

// A preset load's destination, open from fade start while Redux still holds the outgoing preset; gone once `live` moved on with no fade running.
let incoming = null

const sync = () => {
	const { live, camera } = store.getState()
	if (incoming && live.objects !== incoming.from && !window.pixi_transition_active) incoming = null
	const screen = sourceManager.state('screen')
	if (
		!screenPrompt &&
		last.objects === live.objects &&
		last.bags === live.source_settings &&
		last.device === camera.device &&
		last.screen === screen &&
		last.incoming === incoming
	)
		return
	last = { objects: live.objects, bags: live.source_settings, device: camera.device, screen, incoming }

	const device = camera.device
	const ids = [
		...(incoming ? fillPictures(incoming.objects, { device, bags: incoming.bags }) : []),
		...fillPictures(live.objects, { device, bags: live.source_settings }),
	]
		.map(p => p.id)
		.filter((id, i, all) => id && all.indexOf(id) === i)
	const streams = ids.filter(
		id => kindOf(id) && (id !== 'screen' || screen === 'ready' || held.has(id) || screenPrompt)
	)
	screenPrompt = false

	const keepArmed = selectKeepCamerasArmed(store.getState())
	for (const id of streams) {
		if (held.has(id)) continue
		logger.info('open', id)
		held.add(id)
		sourceManager.acquire(id)
		if (keepArmed && kindOf(id) === 'camera') sourceManager.arm(id)
	}
	for (const id of [...held]) {
		if (streams.includes(id)) continue
		held.delete(id)
		sourceManager.release(id)
	}
	// `window.camera_stream` and the camera settings follow the first stream on stage.
	const next = streams[0] ?? 'none'
	if (next !== bridge) {
		bridge = next
		sourceManager.stage(next)
	}
	stageFile(ids.find(isFileSource) ?? null)
}

let started = false
export const startFillStage = () => {
	if (started) return
	started = true
	startFileStage()
	store.subscribe(sync)
	sourceManager.subscribe(sync)
	onFileCatalog(() => {
		last = {}
		sync()
	})
	sync()
}

/** A preset load's `settings` at fade start: its pictures open now, and its first file, when it differs from the transport's, is cued to its start. */
export const stageIncoming = settings => {
	if (!started) return
	const { live, camera } = store.getState()
	incoming = settings?.objects
		? { objects: settings.objects, bags: settings.source_settings, from: live.objects }
		: null
	const file =
		incoming &&
		fillPictures(incoming.objects, { device: camera.device, bags: incoming.bags }).find(p => isFileSource(p.id))
	if (!file) dropCues()
	else if (file.id !== stagedId(NODE)) cue(file.id, file.source, incoming.bags?.[file.source])
	sync()
}

/** Re-reads the stage now: a pick that must open within its click. */
export const syncFillStage = () => {
	if (!started) return
	last = {}
	sync()
}
